import { FC } from 'react';
import { Badge } from '@atoms/Badge/index';
import { Card, Text, Flex, Stack, Title } from '@mantine/core';
import { getFormattedDate } from '@utils/dates';
import { NEWS_HEADER_IMAGE } from 'src/mockdata';
import { useStyles } from './styles';
import { NewsCardProps } from './types';
import Link from 'next/link';
import Image from 'next/image';

export const NewsCard: FC<NewsCardProps> = ({
  news,
  badgeVariant,
  titleOrder,
  hasBadge,
  hasDescription = true,
  hasSubtitle,
  titleVariant,
}) => {
  const { classes } = useStyles();

  if (!news) {
    return null;
  }

  const {
    category,
    header_image,
    title,
    sub_title,
    slug,
    description_text,
    publish_at,
  } = news;

  const leadText = sub_title || description_text?.text || '';

  return (
    <Card className={classes.newsCard} pt={0}>
      {header_image && (
        <Link
          href={`/noticias/${category?.slug}/${slug}`}
          style={{ textDecoration: 'none', width: '100%' }}
        >
          <Card.Section mb={12} className={classes.imageSection}>
            <Flex
              pos="relative"
              w="100%"
              h={{
                base: titleVariant === 'h1' ? 400 : 300,
                lg: titleVariant === 'h1' ? 400 : 200,
              }}
            >
              <Image
                src={header_image.key ?? NEWS_HEADER_IMAGE}
                alt={title}
                sizes="(max-width: 768px) 900px, (max-width: 1200px) 1366px, 700px"
                fill
                style={{
                  objectFit: 'cover',
                }}
                priority
              />
            </Flex>
          </Card.Section>
        </Link>
      )}
      <Card.Section sx={{ wordBreak: 'break-word' }}>
        <Stack spacing={4}>
          {hasBadge && category && (
            <Link
              href={`/noticias/${category?.slug}`}
              style={{ textDecoration: 'none', width: '100%' }}
              aria-label={`link-${category.name}`}
            >
              <Badge
                variant={badgeVariant}
                radius="none"
                color={category.color}
              >
                {category.name}
              </Badge>
            </Link>
          )}
          {publish_at && (
            <Text variant="caption2">{getFormattedDate(publish_at)}</Text>
          )}
          <Link
            href={`/noticias/${category?.slug}/${slug}`}
            style={{ textDecoration: 'none', width: '100%' }}
            aria-label={`link-${title}`}
          >
            <Title
              my={4}
              order={titleOrder}
              variant={titleVariant}
              className={classes.titleSection}
            >
              {title}
            </Title>
          </Link>
          {hasSubtitle ? (
            <Text
              className={classes.subtitleSection}
              mt={2}
              variant="body"
              lineClamp={3}
              component="p"
            >
              {leadText}
            </Text>
          ) : null}
          {hasDescription && description_text ? (
            <Text
              className={classes.subtitleSection}
              mt={2}
              variant="body"
              lineClamp={3}
              component="p"
              dangerouslySetInnerHTML={{ __html: description_text.text }}
            />
          ) : null}
        </Stack>
      </Card.Section>
    </Card>
  );
};
